import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import { AxeActor, AxeStatus } from 'api'
import { Locale, Translation } from 'locale'
import { isNil } from 'lodash'
import { getDateEditorFormat } from 'model'
import { AxeDetail } from 'selectors/axeSelectors'

import {
  createActionsRenderer,
  createGroupedActionRenderer,
  dateFilterParams,
  gridOptionsBase,
  StandardGridColumnId,
  StatusCellRenderer,
  ValidationCellRenderer
} from 'view/components/datagrid'
import { NumericInput } from 'view/components/inputs'
import {
  dateQuickFilterTextGetter,
  getFeeFormatOptions,
  getRateFormatOptions,
  makeDateAndTimeFormatter,
  makeDateFormatter,
  makeFeeFormatter,
  makeNotionalFormatter,
  makeNumberFilterGetter,
  makeNumberFormatter,
  makeNumberQuickFilterTextGetter,
  makeRateFormatter,
  makeRateQuickFilterTextGetter,
  makeStringOrNumberFormatter,
  notionalHeaderValueGetter,
  orderTermFormatter,
  orderTermQuickFilterTextGetter,
  repoTypeFormatter,
  timeQuickFilterTextGetter
} from 'view/shared/datagrid'
import {
  AxeCollateralCombobox,
  CurrencySelectbox,
  ReferenceRateSelectbox,
  RepoTypeSelectbox,
  TermSelectbox,
  TradingDatePicker
} from 'view/shared/entity'
import { AxeActionsButton } from './AxeActionsButton'
import { AxeFieldNames, AxeGridColumnId } from './AxeGridFields'
import * as constants from 'view/styles/constants'
import {
  canEditCurrency,
  canEditFee,
  canEditNotional,
  canEditRate,
  canEditStartConsideration,
  getExcludedTermOptionKeys
} from './axeModelUtils'
import { isNotNil } from 'utils/predicate'
import { FilteredCheckboxHeaderRenderer } from 'view/components/datagrid/renderers/FilteredCheckboxHeaderRenderer'
import { stopPastePropagation } from 'utils'
import { AxeCounterpartyCombobox } from './AxeCounterpartyCombobox'
import { BASE_ASSET_TYPE, BASE_DIRECTION, BASE_ISSUE, BASE_MARKET_TYPE, BASE_REPO_TYPE } from 'view/shared/datagrid/baseColumns'

const getAxeDetailStatusColor = (status: AxeStatus) =>
  ({
    [AxeStatus.DRAFT]: constants.STATUS_COLOR_AWAITING,
    [AxeStatus.READY]: constants.STATUS_COLOR_READY,
    [AxeStatus.SUBMITTED]: constants.STATUS_COLOR_DONE,
    [AxeStatus.CANCELLED]: constants.STATUS_COLOR_INACTIVE
  }[status])

const ALL_POSSIBLE_STATUSES = [
  Translation.TC_ALL,
  Translation.TC_DRAFT,
  Translation.TC_READY,
  Translation.TC_SUBMITTED,
  Translation.TC_CANCELLED,
  Translation.TC_NONE
].map(translation => Locale.translate(translation))

const CHECKBOX_WITH_STATUS_SELECTOR: ColDef<AxeDetail> = {
  colId: StandardGridColumnId.CHECKBOX,
  headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
  headerComponent: FilteredCheckboxHeaderRenderer,
  headerComponentParams: {
    allPossibleStatuses: ALL_POSSIBLE_STATUSES,
    getStatusAsString: (axeDetail: AxeDetail) =>
      ({
        [AxeStatus.CANCELLED]: Locale.translate(Translation.TC_CANCELLED),
        [AxeStatus.DRAFT]: Locale.translate(Translation.TC_DRAFT),
        [AxeStatus.READY]: Locale.translate(Translation.TC_READY),
        [AxeStatus.SUBMITTED]: Locale.translate(Translation.TC_SUBMITTED)
      }[axeDetail?.status]),
    dataTestId: 'AxeSelectAll'
  },
  checkboxSelection: ({ data }) => (data?.availableEvents && data.availableEvents.length > 0) ?? false,
  width: 75,
  maxWidth: 75,
  pinned: 'left',
  lockPinned: true,
  enableRowGroup: false,
  lockPosition: true,
  lockVisible: true,
  filter: false,
  resizable: false,
  sortable: false
}

const CHECKBOX_WITHOUT_STATUS_SELECTOR: ColDef<AxeDetail> = {
  colId: StandardGridColumnId.CHECKBOX,
  headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
  headerCheckboxSelection: true,
  headerCheckboxSelectionFilteredOnly: true,
  checkboxSelection: ({ data }) => (data?.availableEvents && data.availableEvents.length > 0) ?? false,
  width: 75,
  maxWidth: 75,
  pinned: 'left',
  lockPinned: true,
  enableRowGroup: false,
  lockPosition: true,
  lockVisible: true,
  filter: false,
  resizable: false,
  sortable: false
}

const ACTIONS: ColDef<AxeDetail> = {
  colId: StandardGridColumnId.ACTIONS,
  headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
  equals: () => false,
  width: 180,
  minWidth: 180,
  cellClass: 'action-cell',
  headerComponent: createGroupedActionRenderer(AxeActionsButton),
  headerComponentParams: { dataTestId: 'AxeHeaderActions' },
  cellRenderer: createActionsRenderer(AxeActionsButton),
  pinned: 'left',
  lockPinned: true,
  enableRowGroup: false,
  lockVisible: true,
  filter: false,
  sortable: false
}

const STATUS: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.STATUS,
  cellRenderer: StatusCellRenderer,
  cellRendererParams: {
    getStatusColor: (data: AxeDetail) => getAxeDetailStatusColor(data.status)
  },
  valueGetter: params => params.data?.status,
  headerName: Locale.translate(Translation.TC_STATUS),
  width: 180,
  filter: 'agSetColumnFilter'
}

const AXE_ID: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.AXE_ID,
  valueGetter: params => params.data?.axe.axeId,
  headerName: Locale.translate(Translation.TC_AXE_ID),
  width: 180,
  filter: 'agSetColumnFilter'
}

const EXTERNAL_AXE_ID: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.EXTERNAL_AXE_ID,
  valueGetter: params => params.data?.axe.externalAxeId,
  headerName: Locale.translate(Translation.TC_EXTERNAL_AXE_ID),
  width: 180,
  filter: 'agSetColumnFilter'
}

const MY_ENTITY: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.MY_ENTITY,
  valueGetter: params => params.data?.ownTradingEntity?.publishedName,
  headerName: Locale.translate(Translation.TC_MY_ENTITY),
  width: 180,
  filter: 'agSetColumnFilter'
}

const COUNTERPARTY: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.COUNTERPARTY,
  valueGetter: ({ data }) => data?.counterpartyOrganisationLabel,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: AxeFieldNames.RESPONDER_ORGANISATION_IDS
  },
  headerName: Locale.translate(Translation.TC_COUNTERPARTY),
  width: 180,
  minWidth: 100,
  filter: 'agSetColumnFilter',
  editable: true,
  cellEditorParams: {
    inputComponent: AxeCounterpartyCombobox,
    inputParams: (axeDetail: AxeDetail) => ({
      axeDetail,
      selectedLabel: axeDetail.counterpartyOrganisationLabel,
      onPaste: stopPastePropagation,
      dataTestId: 'AxeGridModel-CounterpartyCombobox'
    }),
    updateField: AxeFieldNames.RESPONDER_ORGANISATION_IDS,
    useLoseEquality: true
  }
}

const INSTRUMENT: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.INSTRUMENT,
  valueGetter: params => params.data?.instrument?.name,
  headerName: Locale.translate(Translation.TC_COLLATERAL),
  width: 180,
  filter: 'agSetColumnFilter',
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: (axeDetail: AxeDetail) => axeDetail.instrumentValidationFieldName
  },
  cellEditorParams: {
    inputComponent: AxeCollateralCombobox,
    inputParams: (axeDetail: AxeDetail) => ({
      axeDetail,
      onPaste: stopPastePropagation
    }),
    validationField: (axeDetail: AxeDetail) => axeDetail.instrumentValidationFieldName,
    updateField: AxeFieldNames.INSTRUMENT
  },
  editable: true
}

const ISIN_BUI: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.ISIN_BUI,
  valueGetter: ({ data }) => data?.isinBui,
  headerName: Locale.translate(Translation.TC_ISIN_BUI),
  width: 180,
  filter: 'agSetColumnFilter'
}

const CUSIP: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.CUSIP,
  valueGetter: ({ data }) => data?.cusip,
  headerName: Locale.translate(Translation.TC_CUSIP),
  width: 180,
  filter: 'agSetColumnFilter'
}

const CURRENCY: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.CURRENCY,
  valueGetter: params => params.data?.axe.currency,
  headerName: Locale.translate(Translation.TC_CURRENCY),
  width: 180,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: AxeFieldNames.CURRENCY
  },
  cellEditorParams: {
    inputComponent: CurrencySelectbox,
    inputParams: ({ permittedCurrencies }: AxeDetail) => ({
      permittedCurrencies,
      dataTestId: 'AxeGrid-Currency',
      onPaste: stopPastePropagation
    }),
    updateField: AxeFieldNames.CURRENCY
  },
  editable: ({ data }) => isNotNil(data) && canEditCurrency(data),
  filter: 'agSetColumnFilter'
}

const AXE_NOTIONAL: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.AXE_NOTIONAL,
  valueGetter: params => params.data?.axe.notional,
  valueFormatter: makeNotionalFormatter(),
  width: 180,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  editable: ({ data }) => isNotNil(data) && canEditNotional(data),
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: AxeFieldNames.NOTIONAL
  },
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: () => ({
      formatOptions: makeNotionalFormatter(),
      onPaste: stopPastePropagation
    }),
    updateField: AxeFieldNames.NOTIONAL
  },
  headerName: Locale.translate(Translation.TC_NOTIONAL_SHORT),
  headerValueGetter: notionalHeaderValueGetter
}

const AXE_START_CONSIDERATION: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.AXE_START_CONSIDERATION,
  valueGetter: params => params.data?.axe.startConsideration,
  valueFormatter: makeStringOrNumberFormatter({ minDp: 2 }),
  headerName: Locale.translate(Translation.TC_START_CONSIDERATION),
  width: 180,
  filter: 'agSetColumnFilter',
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: AxeFieldNames.START_CONSIDERATION
  },
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: () => ({
      formatOptions: makeNotionalFormatter(),
      onPaste: stopPastePropagation
    }),
    updateField: AxeFieldNames.START_CONSIDERATION
  },
  editable: ({ data }) => isNotNil(data) && canEditStartConsideration(data)
}

const DIRECTION: ColDef<AxeDetail> = {
  ...BASE_DIRECTION,
  colId: AxeGridColumnId.DIRECTION,
  valueGetter: params => params.data?.direction,
  cellRendererParams: {
    validationField: AxeFieldNames.DIRECTION
  },
  cellEditorParams: {
    ...BASE_DIRECTION.cellEditorParams,
    inputParams: (_axeDetail: AxeDetail, context: any) => ({
      directionFormat: context.userPreference.directionFormat,
      dataTestId: 'AxeGrid-Direction',
      onPaste: stopPastePropagation
    }),
    updateField: AxeFieldNames.DIRECTION
  },
  width: 200,
  editable: true
}

const START_DATE: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.START_DATE,
  headerName: Locale.translate(Translation.TC_START_DATE),
  width: 120,
  valueGetter: params => params.data?.axe.startDate,
  valueFormatter: makeDateFormatter(),
  filter: 'agSetColumnFilter',
  ...dateFilterParams,
  getQuickFilterText: dateQuickFilterTextGetter,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: AxeFieldNames.START_DATE
  },
  cellEditorParams: {
    inputComponent: TradingDatePicker,
    inputParams: ({ axe }: AxeDetail, context: any) => ({
      currency: axe.currency,
      dateFormat: getDateEditorFormat(context.userPreference.dateFormat),
      readOnly: isNil(axe.currency),
      dataTestId: 'AxeGrid-StartDate',
      onPaste: stopPastePropagation
    }),
    updateField: AxeFieldNames.START_DATE
  },
  editable: true
}

const END_DATE: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.END_DATE,
  headerName: Locale.translate(Translation.TC_END_DATE),
  width: 200,
  valueGetter: params => params.data?.axe.endDate,
  valueFormatter: makeDateFormatter(),
  filter: 'agSetColumnFilter',
  ...dateFilterParams,
  getQuickFilterText: dateQuickFilterTextGetter,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: AxeFieldNames.END_DATE
  },
  cellEditorParams: {
    inputComponent: TradingDatePicker,
    inputParams: ({ axe }: AxeDetail, context: any) => ({
      currency: axe.currency,
      dateFormat: getDateEditorFormat(context.userPreference.dateFormat),
      readOnly: isNil(axe.currency),
      dataTestId: 'AxeGrid-EndDate',
      onPaste: stopPastePropagation
    }),
    updateField: AxeFieldNames.END_DATE
  },
  editable: true
}

const TERM: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.TERM,
  valueGetter: params => params.data?.axe.term,
  headerName: Locale.translate(Translation.TC_TERM),
  width: 200,
  filter: 'agSetColumnFilter',
  valueFormatter: orderTermFormatter,
  getQuickFilterText: orderTermQuickFilterTextGetter,
  equals: () => false,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: AxeFieldNames.TERM,
    dataTestId: 'StagingOrdersGridModel-Term-ValidationCellRenderer'
  },
  cellEditorParams: {
    inputComponent: TermSelectbox,
    inputParams: (axeDetail: AxeDetail) => {
      const excludeOptionKeys = getExcludedTermOptionKeys(axeDetail)
      return {
        readOnly: !axeDetail.axe.currency,
        dataTestId: 'AxeGrid-Term',
        excludeOptionKeys,
        onPaste: stopPastePropagation
      }
    },
    updateField: AxeFieldNames.TERM
  },
  editable: true
}

const REPO_TYPE: ColDef<AxeDetail> = {
  ...BASE_REPO_TYPE,
  colId: AxeGridColumnId.REPO_TYPE,
  valueGetter: params => params.data?.axe.repoType,
  valueFormatter: repoTypeFormatter,
  headerName: Locale.translate(Translation.TC_REPO_TYPE),
  width: 180,
  filter: 'agSetColumnFilter',
  equals: () => false,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: AxeFieldNames.REPO_TYPE
  },
  cellEditorParams: {
    inputComponent: RepoTypeSelectbox,
    inputParams: (axeDetail: AxeDetail) => ({
      supportedRepoTypes: axeDetail.availableRepoTypes,
      dataTestId: 'AxeGrid-RepoType',
      onPaste: stopPastePropagation
    }),
    updateField: AxeFieldNames.REPO_TYPE
  },
  editable: true
}

const MY_FIRM: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.MY_FIRM,
  valueGetter: params => params.data?.ownOrganisation?.legalName,
  headerName: Locale.translate(Translation.TC_MY_FIRM),
  width: 180,
  filter: 'agSetColumnFilter'
}

const MARKET_TYPE: ColDef<AxeDetail> = {
  ...BASE_MARKET_TYPE,
  valueGetter: params => params.data?.axe.marketType,
  colId: AxeGridColumnId.MARKET_TYPE,
  width: 180,
  headerName: Locale.translate(Translation.TC_MARKET_TYPE)
}

const PRODUCT: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.PRODUCT,
  valueGetter: ({ data }) => data?.product?.name,
  headerName: Locale.translate(Translation.TC_PRODUCT),
  width: 180,
  filter: 'agSetColumnFilter'
}

const SECTOR: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.SECTOR,
  valueGetter: ({ data }) => data?.productSector?.name,
  headerName: Locale.translate(Translation.TC_SECTOR),
  width: 180,
  filter: 'agSetColumnFilter'
}

const COUPON: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.COUPON,
  valueGetter: ({ data }) => data?.coupon,
  headerName: Locale.translate(Translation.TC_COUPON),
  width: 180,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({ percent: true, minDp: 1, decimalShift: 2 }),
  filterValueGetter: makeNumberFilterGetter({ percent: true, minDp: 1, decimalShift: 2 }),
  getQuickFilterText: makeNumberQuickFilterTextGetter({ percent: true })
}

const ISSUER: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.ISSUER,
  valueGetter: ({ data }) => data?.issuer,
  headerName: Locale.translate(Translation.TC_ISSUER),
  width: 180,
  filter: 'agSetColumnFilter'
}

const CREATED_BY: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.CREATED_BY,
  valueGetter: ({ data }) => data?.createdBy,
  headerName: Locale.translate(Translation.TC_CREATED_BY),
  width: 180,
  filter: 'agSetColumnFilter'
}

const UPDATED_BY: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.UPDATED_BY,
  valueGetter: ({ data }) => data?.updatedBy,
  headerName: Locale.translate(Translation.TC_LAST_UPDATED_BY),
  width: 180,
  filter: 'agSetColumnFilter'
}

const LAST_UPDATED_TIME: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.LAST_UPDATED_TIME,
  valueGetter: ({ data }) => data?.updatedAt,
  valueFormatter: makeDateAndTimeFormatter(),
  headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT_TIME),
  width: 180,
  filter: 'agSetColumnFilter'
}

const CREATION_TIME: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.CREATION_TIME,
  valueGetter: ({ data }) => data?.createdAt,
  valueFormatter: makeDateAndTimeFormatter(),
  headerName: Locale.translate(Translation.TC_CREATION_TIME),
  width: 180,
  getQuickFilterText: timeQuickFilterTextGetter,
  ...dateFilterParams
}

const RATE: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.RATE,
  valueGetter: params => params.data?.axe.rate,
  valueFormatter: makeRateFormatter(),
  getQuickFilterText: makeRateQuickFilterTextGetter(),
  headerName: Locale.translate(Translation.TC_RATE),
  width: 180,
  filter: 'agSetColumnFilter',
  editable: ({ data }) => (isNotNil(data) ? canEditRate(data) : false),
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: AxeFieldNames.RATE
  },
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: ({ referenceRate }: AxeDetail) => ({
      formatOptions: getRateFormatOptions(referenceRate),
      onPaste: stopPastePropagation
    }),
    updateField: AxeFieldNames.RATE
  }
}

const FEE: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.FEE,
  valueGetter: params => params.data?.axe.fee,
  valueFormatter: makeFeeFormatter(),
  filterValueGetter: makeNumberFilterGetter(getFeeFormatOptions()),
  getQuickFilterText: makeNumberQuickFilterTextGetter(getFeeFormatOptions()),
  headerName: Locale.translate(Translation.TC_FEE),
  width: 180,
  filter: 'agSetColumnFilter',
  editable: ({ data }) => (isNotNil(data) ? canEditFee(data) : false),
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: AxeFieldNames.FEE
  },
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: () => ({
      formatOptions: getFeeFormatOptions(),
      onPaste: stopPastePropagation
    }),
    updateField: AxeFieldNames.FEE
  }
}

const REFERENCE_RATE: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.REFERENCE_RATE,
  valueGetter: params => params.data?.axe.referenceRate,
  getQuickFilterText: makeRateQuickFilterTextGetter(),
  width: 180,
  type: 'numericColumn',
  filter: 'agSetColumnFilter',
  headerName: Locale.translate(Translation.TC_REFERENCE_RATE),

  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: AxeFieldNames.REFERENCE_RATE
  },
  cellEditorParams: {
    inputComponent: ReferenceRateSelectbox,
    inputParams: ({ productSector }: AxeDetail) => ({
      referenceRateIds: Object.keys(productSector?.defaultFloatCrystallisationDaysByReferenceRateId || {}),
      onPaste: stopPastePropagation
    }),
    updateField: AxeFieldNames.REFERENCE_RATE
  },
  editable: true
}

const MY_ENTITY_SHORT_NAME: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.MY_ENTITY_SHORT_NAME,
  valueGetter: ({ data }) => data?.myEntityShortName,
  headerName: Locale.translate(Translation.TC_MY_ENTITY_SHORT_NAME),
  width: 150,
  filter: 'agSetColumnFilter'
}

const MY_FICC_ENTITY_SHORT_NAME: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.MY_FICC_ENTITY_SHORT_NAME,
  valueGetter: ({ data }) => data?.ownTradingEntity?.ficcShortName,
  headerName: Locale.translate(Translation.TC_MY_FICC_ENTITY_SHORT_NAME),
  width: 150,
  filter: 'agSetColumnFilter'
}

const ASSET_TYPE: ColDef<AxeDetail> = {
  ...BASE_ASSET_TYPE,
  colId: AxeGridColumnId.ASSET_TYPE,
  valueGetter: ({ data }) => data?.axe.assetType,
  width: 150
}

const FACTOR: ColDef<AxeDetail> = {
  colId: AxeGridColumnId.FACTOR,
  valueGetter: ({ data }) => data?.axe.factor,
  headerName: Locale.translate(Translation.TC_FACTOR),
  width: 150,
  filter: 'agSetColumnFilter'
}

const ISSUE: ColDef<AxeDetail> = {
  ...BASE_ISSUE,
  colId: AxeGridColumnId.ISSUE,
  valueGetter: ({ data }) => data?.axe.collateralIssue
}

const MARKET_TRADE_DATE: ColDef = {
  colId: AxeGridColumnId.MARKET_TRADE_DATE,
  headerName: Locale.translate(Translation.TC_MARKET_TRADE_DATE),
  valueGetter: ({ data }) => data?.axe?.marketTradeDate,
  width: 150,
  filter: 'agSetColumnFilter',
  valueFormatter: makeDateFormatter()
}

const getVisibleColumns = (axeActor: AxeActor, isAdmin: boolean) => [
  ...(axeActor === AxeActor.INITIATOR && !isAdmin ? [CHECKBOX_WITH_STATUS_SELECTOR] : []),
  ...(axeActor === AxeActor.RESPONDER ? [CHECKBOX_WITHOUT_STATUS_SELECTOR] : []),
  ...(isAdmin ? [] : [ACTIONS]),
  STATUS,
  AXE_ID,
  EXTERNAL_AXE_ID,
  MY_ENTITY,
  COUNTERPARTY,
  INSTRUMENT,
  ISIN_BUI,
  CUSIP,
  CURRENCY,
  AXE_NOTIONAL,
  AXE_START_CONSIDERATION,
  DIRECTION,
  START_DATE,
  END_DATE,
  MARKET_TRADE_DATE,
  TERM,
  REPO_TYPE
]

const getHiddenColumns = (isAdmin: boolean): ColDef[] =>
  ([
    MY_FIRM,
    MARKET_TYPE,
    PRODUCT,
    SECTOR,
    COUPON,
    ISSUER,
    LAST_UPDATED_TIME,
    UPDATED_BY,
    CREATION_TIME,
    CREATED_BY,
    RATE,
    REFERENCE_RATE,
    FEE,
    MY_ENTITY_SHORT_NAME,
    ASSET_TYPE,
    FACTOR,
    ISSUE,
    ...(isAdmin ? [MY_FICC_ENTITY_SHORT_NAME] : [])
  ] as ColDef[])
    .map(colDef => ({
      ...colDef,
      hide: true
    }))
    .sort((a, b) => (a.headerName && b.headerName ? a.headerName.localeCompare(b.headerName) : 0))

export const getAxeGridColumnDefs: (axeActor: AxeActor, isAdmin: boolean) => (ColGroupDef | ColDef)[] = (axeActor, isAdmin) =>
  getVisibleColumns(axeActor, isAdmin).concat(getHiddenColumns(isAdmin))

export const axeGridOptions: GridOptions<AxeDetail> = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.axe.axeId,
  rowClassRules: {}
}
